import React, { useState, useEffect } from 'react';
import {
  Users, Package, DollarSign, Settings, Trophy, Shield,
  AlertTriangle, Unlock, Lock, Loader2, Database, Search
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import StatCard from '@/components/dashboard/StatCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { adminDashboard, listarEmpresas } from '@/services/api';
import api from '@/services/api';

const DashboardSuperAdmin = () => {
  const [stats, setStats] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [filtro, setFiltro] = useState('');
  const navigate = useNavigate();
  const { usuario } = useAuth();

  // Modal Liberar Manual
  const [openLiberar, setOpenLiberar] = useState(false);
  const [empresaAlvo, setEmpresaAlvo] = useState(null);
  const [diasLiberacao, setDiasLiberacao] = useState(30);
  const [motivoLiberacao, setMotivoLiberacao] = useState('');
  const [loadingAcao, setLoadingAcao] = useState(false);

  // Modal Limpar Base
  const [openCleanup, setOpenCleanup] = useState(false);
  const [confirmTexto, setConfirmTexto] = useState('');
  const [cleanupResult, setCleanupResult] = useState(null);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      const [dashResponse, empresasResponse] = await Promise.all([
        adminDashboard(),
        listarEmpresas(),
      ]);
      setStats(dashResponse.data);
      setEmpresas(empresasResponse.data);
    } catch {
      toast.error('Erro ao carregar dados');
    }
  };

  const abrirLiberar = (emp) => {
    setEmpresaAlvo(emp);
    setDiasLiberacao(30);
    setMotivoLiberacao('');
    setOpenLiberar(true);
  };

  const handleLiberar = async () => {
    if (!empresaAlvo) return;
    setLoadingAcao(true);
    try {
      await api.post(`/admin/empresas/${empresaAlvo.id}/liberar-manual`, {
        dias: parseInt(diasLiberacao, 10) || 30,
        motivo: motivoLiberacao.trim() || null,
      });
      toast.success(`Empresa ${empresaAlvo.razao_social} liberada por ${diasLiberacao} dias`);
      setOpenLiberar(false);
      carregarDados();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao liberar');
    } finally {
      setLoadingAcao(false);
    }
  };

  const handleRevogar = async (emp) => {
    if (!window.confirm(`Revogar liberação manual de ${emp.razao_social}?`)) return;
    try {
      await api.post(`/admin/empresas/${emp.id}/revogar-liberacao`);
      toast.success('Liberação revogada');
      carregarDados();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao revogar');
    }
  };

  const handleCleanup = async () => {
    if (confirmTexto !== 'APAGAR') {
      toast.error('Digite "APAGAR" para confirmar');
      return;
    }
    setLoadingAcao(true);
    try {
      const res = await api.post('/admin/cleanup-base', { confirmacao: 'APAGAR' });
      setCleanupResult(res.data);
      toast.success(`Base limpa: ${res.data.total_apagado} documentos apagados`);
      carregarDados();
      setConfirmTexto('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao limpar base');
    } finally {
      setLoadingAcao(false);
    }
  };

  const empresasFiltradas = empresas.filter((e) => {
    const q = filtro.trim().toLowerCase();
    if (!q) return true;
    return (
      (e.razao_social || '').toLowerCase().includes(q) ||
      (e.cnpj || '').toLowerCase().includes(q) ||
      (e.nome_fantasia || '').toLowerCase().includes(q) ||
      (e.perfil || '').toLowerCase().includes(q)
    );
  });

  return (
    <DashboardLayout title="Dashboard Super Admin">
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard title="Total Empresas" value={stats.total_empresas} icon={Users} color="blue" />
          <StatCard title="Clientes" value={stats.total_clientes} icon={Users} color="emerald" />
          <StatCard title="Entregadores" value={stats.total_entregadores} icon={Package} color="amber" />
          <StatCard title="Receita Total" value={`R$ ${stats.receita_total?.toFixed(2) || '0.00'}`} icon={DollarSign} color="emerald" />
        </div>
      )}

      {/* Navegação Rápida */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/configuracoes')}
          className="bg-gradient-to-br from-orange-600 to-amber-500 text-white rounded-2xl p-6 text-left hover:shadow-lg transition-shadow"
          data-testid="btn-nav-configuracoes"
        >
          <div className="flex items-center gap-3">
            <Settings className="w-6 h-6" />
            <div>
              <h3 className="font-bold">Configurações</h3>
              <p className="text-sm text-orange-50">Tempo, raio GPS e ranking</p>
            </div>
          </div>
        </button>
        <button
          onClick={() => navigate('/admin/ranking')}
          className="bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-2xl p-6 text-left hover:shadow-lg transition-shadow"
          data-testid="btn-nav-ranking"
        >
          <div className="flex items-center gap-3">
            <Trophy className="w-6 h-6" />
            <div>
              <h3 className="font-bold">Ranking</h3>
              <p className="text-sm text-amber-50">Entregadores e avaliações</p>
            </div>
          </div>
        </button>
        {usuario?.perfil === 'master_admin' && (
          <button
            onClick={() => navigate('/dashboard/master-admin')}
            className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-2xl p-6 text-left hover:shadow-lg transition-shadow"
            data-testid="btn-nav-master-admin"
          >
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6" />
              <div>
                <h3 className="font-bold">Validação Cadastral</h3>
                <p className="text-sm text-emerald-50">Aprovar empresas</p>
              </div>
            </div>
          </button>
        )}
        {usuario?.perfil === 'master_admin' && (
          <button
            onClick={() => navigate('/admin/usuarios')}
            className="bg-gradient-to-br from-indigo-500 to-violet-600 text-white rounded-2xl p-6 text-left hover:shadow-lg transition-shadow"
            data-testid="btn-nav-usuarios"
          >
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6" />
              <div>
                <h3 className="font-bold">Gestão de Usuários</h3>
                <p className="text-sm text-indigo-50">Criar e listar usuários</p>
              </div>
            </div>
          </button>
        )}
      </div>

      {/* Zona Perigosa - Limpar Base (somente master_admin) */}
      {usuario?.perfil === 'master_admin' && (
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-5 mb-6 flex items-center justify-between gap-4" data-testid="danger-zone">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-red-600 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-red-900">Zona Perigosa — Limpar Base de Dados</h3>
              <p className="text-sm text-red-700">Apaga TODOS os dados de teste (empresas, entregas, pagamentos). Mantém apenas você e as configurações.</p>
            </div>
          </div>
          <Button
            onClick={() => { setConfirmTexto(''); setCleanupResult(null); setOpenCleanup(true); }}
            className="bg-red-600 hover:bg-red-700 text-white flex-shrink-0"
            data-testid="btn-open-cleanup"
          >
            <Database className="w-4 h-4 mr-2" />
            Limpar Base
          </Button>
        </div>
      )}

      {/* Empresas Cadastradas + Liberação Manual */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between gap-4">
          <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Empresas Cadastradas <span className="text-sm font-normal text-slate-500">({empresasFiltradas.length})</span>
          </h2>
          <div className="relative w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Buscar por CNPJ, nome, perfil"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              className="pl-9 h-9"
              data-testid="input-filtro-empresas"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">CNPJ/NIF</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Empresa</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Perfil</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Validação</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Assinatura</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-600 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {empresasFiltradas.map((emp) => (
                <tr key={emp.id} className="hover:bg-slate-50" data-testid={`row-empresa-${emp.id}`}>
                  <td className="px-4 py-3 text-sm font-mono">{emp.cnpj}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium text-slate-900">{emp.razao_social}</div>
                    {emp.nome_fantasia && <div className="text-xs text-slate-500">{emp.nome_fantasia}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm capitalize">{emp.perfil}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      emp.status_validacao === 'aprovado' ? 'bg-green-100 text-green-800' :
                      emp.status_validacao === 'rejeitado' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {(emp.status_validacao || '').replace(/_/g, ' ') || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        emp.assinatura_ativa ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {emp.assinatura_ativa ? 'Ativa' : 'Inativa'}
                      </span>
                      {emp.liberacao_manual && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700" title={emp.liberacao_manual_motivo || 'Liberado manualmente'}>
                          Liberado manual
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      {emp.perfil !== 'master_admin' && (!emp.assinatura_ativa || !emp.liberacao_manual) ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => abrirLiberar(emp)}
                          className="border-orange-300 text-orange-700 hover:bg-orange-50"
                          data-testid={`btn-liberar-${emp.id}`}
                        >
                          <Unlock className="w-3 h-3 mr-1" />
                          Liberar
                        </Button>
                      ) : null}
                      {emp.liberacao_manual && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRevogar(emp)}
                          className="border-red-300 text-red-700 hover:bg-red-50"
                          data-testid={`btn-revogar-${emp.id}`}
                        >
                          <Lock className="w-3 h-3 mr-1" />
                          Revogar
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {empresasFiltradas.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-slate-500">
                    Nenhuma empresa encontrada
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Liberar Manual */}
      <Dialog open={openLiberar} onOpenChange={setOpenLiberar}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Unlock className="w-5 h-5 text-orange-600" />
              Liberar Manualmente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {empresaAlvo && (
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Empresa</p>
                <p className="font-medium text-slate-900">{empresaAlvo.razao_social}</p>
                <p className="text-xs text-slate-500 font-mono">{empresaAlvo.cnpj}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Período (dias)</label>
              <div className="flex gap-2">
                {[7, 30, 60, 90, 180].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDiasLiberacao(d)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border ${
                      diasLiberacao === d ? 'bg-orange-600 border-orange-600 text-white' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid={`btn-dias-${d}`}
                  >
                    {d}d
                  </button>
                ))}
              </div>
              <Input
                type="number"
                min={1}
                max={365}
                value={diasLiberacao}
                onChange={(e) => setDiasLiberacao(e.target.value)}
                className="mt-2"
                data-testid="input-dias-personalizado"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Motivo (opcional)</label>
              <Textarea
                value={motivoLiberacao}
                onChange={(e) => setMotivoLiberacao(e.target.value)}
                rows={3}
                placeholder="Ex: Cliente VIP, cortesia inicial, parceria"
                data-testid="textarea-motivo-liberacao"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setOpenLiberar(false)} className="flex-1" disabled={loadingAcao}>
                Cancelar
              </Button>
              <Button
                onClick={handleLiberar}
                disabled={loadingAcao}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
                data-testid="btn-confirmar-liberar"
              >
                {loadingAcao ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Liberando...</> : <><Unlock className="w-4 h-4 mr-2" /> Confirmar Liberação</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Cleanup */}
      <Dialog open={openCleanup} onOpenChange={setOpenCleanup}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Limpar Base de Dados
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!cleanupResult ? (
              <>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-900">
                  <p className="font-bold mb-2">⚠️ Esta operação é IRREVERSÍVEL</p>
                  <p className="mb-2">Serão apagados:</p>
                  <ul className="list-disc pl-5 text-xs space-y-0.5">
                    <li>Todas as empresas (exceto a sua)</li>
                    <li>Todos os usuários (exceto você)</li>
                    <li>Entregas, pagamentos, avaliações, contratos assinados</li>
                    <li>Ranking, localizações, documentos, mensagens, créditos</li>
                  </ul>
                  <p className="mt-2 font-medium">Preservado: seu acesso de Master Admin e as configurações globais.</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">
                    Digite <strong className="text-red-600 font-mono">APAGAR</strong> para confirmar
                  </label>
                  <Input
                    value={confirmTexto}
                    onChange={(e) => setConfirmTexto(e.target.value)}
                    placeholder="APAGAR"
                    className="font-mono text-center text-lg uppercase"
                    data-testid="input-confirm-apagar"
                    disabled={loadingAcao}
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setOpenCleanup(false)} className="flex-1" disabled={loadingAcao}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleCleanup}
                    disabled={confirmTexto !== 'APAGAR' || loadingAcao}
                    className="flex-1 bg-red-600 hover:bg-red-700"
                    data-testid="btn-confirmar-cleanup"
                  >
                    {loadingAcao ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Apagando...</> : <><Database className="w-4 h-4 mr-2" /> Apagar Tudo</>}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-900">
                  <p className="font-bold mb-2">✅ Limpeza concluída</p>
                  <p>Total apagado: <strong>{cleanupResult.total_apagado}</strong> documentos</p>
                  <p>Admin preservado: {cleanupResult.admin_preservado}</p>
                </div>
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-600">Ver detalhes por coleção</summary>
                  <pre className="mt-2 p-2 bg-slate-50 rounded overflow-x-auto">
                    {JSON.stringify(cleanupResult.detalhes, null, 2)}
                  </pre>
                </details>
                <Button onClick={() => setOpenCleanup(false)} className="w-full">Fechar</Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default DashboardSuperAdmin;
